import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  triggerIngestionApiIngestionTriggerPost,
  type TriggerIngestionRequest,
  type TriggerIngestionResponse,
} from "@noa/api-core";
import { UploadQueryKey } from "../../query-keys";
import { reportError } from "../../typed-client";
import { ApiError } from "../../errors";

/**
 * Trigger a connector sync (Maximo, SharePoint, ConformIT, Google Drive, or a custom job).
 *
 * Invalidates the job list so the new job appears in Job History without a
 * manual refresh — the sync panels previously called the endpoint through a
 * bare useMutation, so the table went stale after every trigger.
 *
 * The response is returned unchanged: callers read either `task_id` or
 * `job_id` depending on the panel.
 */
export function useTriggerIngestion() {
  const queryClient = useQueryClient();

  return useMutation<TriggerIngestionResponse, ApiError, TriggerIngestionRequest>({
    mutationFn: async (body) => {
      const { data } = await triggerIngestionApiIngestionTriggerPost({
        body,
        throwOnError: true,
      });
      return data;
    },
    onError: reportError,
    onSuccess: () => {
      // Invalidate job list to show the new job
      void queryClient.invalidateQueries({ queryKey: UploadQueryKey.list() });
    },
  });
}
